/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { If, Then, Else, When } from "react-if";
import { Button, Spinner, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { ContactsInput } from "./ContactsInput/ContactsInput";

type FormData = { personId: string };

type Props = {
  person:
    | {
        id: string;
        name: string;
      }
    | null
    | undefined;
  isLoading: boolean;
  onSubmit: SubmitHandler<FormData>;
  readonly: boolean;
  canDelete?: boolean;
};

export function InlineContactsUpdate({
  person,
  isLoading,
  onSubmit,
  readonly,
  canDelete = true,
}: Props) {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [contacts, setContacts] = React.useState<string[]>([]);
  const [isDirty, setIsDirty] = React.useState(false);

  const { register, handleSubmit, setValue } = useForm();

  const closeEditMode = () => {
    setIsEditMode(false);
    setIsDirty(false);
  };

  if (isLoading) {
    return (
      <FlexContainer>
        <Spinner size="sm" />
        <Text
          weight="regular"
          tag="div"
          css={{ marginLeft: theme.spacing.spacing04 }}
        >
          Loading...
        </Text>
      </FlexContainer>
    );
  }

  const propertyId = "personId";

  if (readonly) {
    return (
      <FlexContainer alignItems="center">
        <Text as="div" m="0" mr={theme.spacing.spacing02}>
          {person?.name}
        </Text>
      </FlexContainer>
    );
  }

  if (isEditMode) {
    return (
      <form
        css={{ display: "block", width: "100%" }}
        onSubmit={handleSubmit<FormData>((data) => {
          closeEditMode();
          onSubmit(data);
        })}
      >
        <input type="hidden" {...register("id")} defaultValue={person?.id} />
        <FlexContainer>
          <ContactsInput
            small
            value={contacts}
            onChange={(newPerson) => {
              setContacts([newPerson]);
              setValue(propertyId, newPerson);
              setIsDirty(true);
            }}
          />
          <If condition={isDirty}>
            <Then>
              <Button
                ml={theme.spacing.spacing02}
                type="submit"
                icon="save"
                size="md"
                severity="low"
              >
                Save
              </Button>
            </Then>
            <Else>
              <When condition={canDelete}>
                <Button
                  ml={theme.spacing.spacing02}
                  icon="trash"
                  size="md"
                  severity="low"
                  type="submit"
                  onClick={() => {
                    setContacts([]);
                    setValue(propertyId, []);
                  }}
                >
                  Delete
                </Button>
              </When>
            </Else>
          </If>
          <Button
            ml={theme.spacing.spacing02}
            icon="close"
            size="md"
            severity="low"
            onClick={() => {
              closeEditMode();
            }}
          >
            Cancel
          </Button>
        </FlexContainer>
      </form>
    );
  }

  return (
    <FlexContainer alignItems="center">
      <Text as="div" m="0" mr={theme.spacing.spacing02}>
        {person?.name}
      </Text>
      <Button
        data-testid="edit-contacts"
        severity="low"
        size="sm"
        icon="edit"
        aria-pressed="false"
        css={{ margin: 0, borderRadius: "22px", padding: "0.3rem" }}
        onClick={() => {
          setContacts([person?.id ?? ""]);
          setIsEditMode(true);
        }}
      />
    </FlexContainer>
  );
}
